/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import cx from "classnames";

import {
  IApplicationMenuScreenModel,
  IConfigurationModel,
  ScreenType,
} from "@nf/common";
import { ROUTES } from "@nf/constants";

import ApplicationMenu from "components/ApplicationMenu";
import ApplicationMenuDropdown from "../ApplicationMenuDropdown/ApplicationMenuDropdown";
import Button from "components/Button";
import {
  useDunning,
  useIsAuthenticated,
  useScrolled,
  useSyncCompleted,
} from "hooks";
import { SearchButton, SearchInput } from "screens/SearchScreen";

import HeaderLogo from "./components/HeaderLogo";
import HeaderUserInfo from "./components/HeaderUserInfo";
import { getVisibilityClasses } from "./utils";
import type { NavConfig } from "./types";

import BurgerIcon from "resources/icons/menu.svg";

import styles from "./AppHeader.module.scss";

const DunningBanner = dynamic(() => import("components/DunningBanner"), {
  ssr: false,
});

export interface AppHeaderProps {
  configuration?: IConfigurationModel;
  navigation?: NavConfig;
}

export const AppHeader = ({
  configuration,
  navigation = {
    logo: {
      show: true,
      visibility: { phone: false, tablet: true, desktop: true, large: true },
    },
    menu: { show: true },
    dropdown: { show: true },
    search: {
      show: true,
      visibility: { phone: false, tablet: true, desktop: true, large: true },
    },
    userInfo: {
      show: true,
      visibility: { phone: false, tablet: true, desktop: true, large: true },
    },
  },
}: AppHeaderProps) => {
  const { t } = useTranslation("translations");
  const { isLoggedIn, isChild } = useIsAuthenticated();
  const isAuthProcessCompleted = useSyncCompleted();
  const { isInDunning, wasDunningDialogDismissed } = useDunning();
  const isPageScrolledDown = useScrolled();

  const router = useRouter();
  const query = router.query as { id: string; channelRoute?: string };
  const isSearchPage = router.asPath.includes(ROUTES.SEARCH_SCREEN);

  const [channelsVisible, setChannelsVisible] = useState(false);
  const toggleChannelsVisibility = () => {
    setChannelsVisible(!channelsVisible);
  };

  const applicationMenu = configuration?.Screens?.[
    ScreenType.ApplicationMenu
  ] as IApplicationMenuScreenModel | undefined;

  const components = applicationMenu?.Components?.filter((component) => {
    if (!component.IsVisible) {
      return false;
    }

    if (component.IsAgeRestricted && isChild) {
      return false;
    }

    return component.Action?.ScreenTypeCode !== ScreenType.Search;
  });

  const activeComponent =
    components?.find(
      (component) =>
        component.Id === query.id || component.Id === query.channelRoute
    ) || components?.[0];

  return (
    <div
      className={cx(styles.navigation, navigation.className, {
        [styles.background]: isPageScrolledDown,
        [styles.expanded]: channelsVisible,
      })}
    >
      {isInDunning && wasDunningDialogDismissed && (
        <DunningBanner visible={!isPageScrolledDown} />
      )}

      <header
        className={cx(
          styles.header,
          getVisibilityClasses(navigation?.visibility)
        )}
      >
        <div className={styles.left}>
          {navigation?.logo?.show && (
            <HeaderLogo
              className={cx(
                styles.logo,
                getVisibilityClasses(navigation.logo.visibility)
              )}
            />
          )}

          {/* Mobile */}
          {navigation?.dropdown?.show && (
            <ApplicationMenuDropdown
              className={getVisibilityClasses(navigation.dropdown.visibility)}
              components={components}
              activeComponent={activeComponent}
              toggled={channelsVisible}
              onClick={toggleChannelsVisibility}
            />
          )}

          {/* Desktop */}
          {navigation?.menu?.show && (
            <ApplicationMenu
              className={getVisibilityClasses(navigation.menu.visibility)}
              components={components}
              activeComponent={activeComponent}
            />
          )}

          {navigation?.custom?.left}
        </div>

        <div className={styles.right}>
          {navigation?.search?.show && (
            <span
              className={getVisibilityClasses(navigation.search.visibility)}
            >
              {isSearchPage ? <SearchInput /> : <SearchButton />}
            </span>
          )}

          {navigation?.userInfo?.show && isLoggedIn && !isChild && (
            <Button
              href={ROUTES.SETTINGS_PERSONAL_DETAILS_SCREEN}
              size="small"
              variant="tertiary"
              icon={<BurgerIcon />}
              className={getVisibilityClasses(navigation.userInfo.visibility)}
            />
          )}

          {navigation?.userInfo?.show && (
            <span
              className={getVisibilityClasses(navigation.userInfo.visibility)}
            >
              {!isAuthProcessCompleted ? (
                <div className={styles.skeleton} />
              ) : isLoggedIn ? (
                <HeaderUserInfo />
              ) : (
                <Button href={ROUTES.LOGIN} size="small" variant="primary">
                  {t("web_login_button")}
                </Button>
              )}
            </span>
          )}

          {navigation?.custom?.right}
        </div>
      </header>
    </div>
  );
};

export default AppHeader;
